import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { usePageTitle } from '../../../hooks/shared';
import { authoritiesAPI } from '../api/authorities';

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-neutral-100 text-neutral-500',
};

export default function AdminSubmissions() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [filter, setFilter] = useState('pending');
  const [page, setPage] = useState(1);
  const [rejecting, setRejecting] = useState(null); // submissionId being rejected with note
  const [rejectNote, setRejectNote] = useState('');

  usePageTitle('Admin — URL Submissions');

  const { data, isLoading } = useQuery({
    queryKey: ['admin-submissions', filter, page],
    queryFn: () => authoritiesAPI.listSubmissions({ status: filter, page }),
  });

  const reviewMutation = useMutation({
    mutationFn: ({ id, action, notes }) => authoritiesAPI.reviewSubmission(id, action, notes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-submissions'] });
      setRejecting(null);
      setRejectNote('');
    },
  });

  const submissions = data?.submissions ?? [];
  const pagination = data?.pagination ?? {};

  return (
    <div className="bg-neutral-50 min-h-screen py-8">
      <div className="container-custom">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-serif font-bold text-neutral-900">URL Submissions</h1>
            <p className="text-neutral-500 text-base mt-0.5">
              Review crowd-sourced website suggestions
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/authorities')}
            className="text-base text-primary-600 hover:text-primary-700 font-medium"
          >
            Authority editor →
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1 bg-white rounded-xl border border-neutral-200 p-1 w-fit mb-6 shadow-sm">
          {['pending', 'approved', 'rejected'].map((s) => (
            <button
              key={s}
              onClick={() => {
                setFilter(s);
                setPage(1);
              }}
              className={`px-4 py-1.5 text-base font-medium rounded-lg transition-colors capitalize ${filter === s ? 'bg-primary-600 text-white' : 'text-neutral-600 hover:bg-neutral-100'}`}
            >
              {s}
            </button>
          ))}
        </div>

        {isLoading && (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-neutral-200 p-5 animate-pulse h-20"
              />
            ))}
          </div>
        )}

        {!isLoading && submissions.length === 0 && (
          <div className="text-center py-16 text-neutral-500">No {filter} submissions.</div>
        )}

        {!isLoading && submissions.length > 0 && (
          <div className="space-y-3">
            {submissions.map((sub) => (
              <div
                key={sub.id}
                className="bg-white rounded-2xl border border-neutral-200 p-5 shadow-sm"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    {/* Jurisdiction */}
                    {sub.authority ? (
                      <p className="font-semibold text-neutral-900 text-base">
                        {sub.authority.name}{' '}
                        <span className="text-neutral-400 font-normal">
                          ({sub.authority.state})
                        </span>
                      </p>
                    ) : (
                      <p className="font-semibold text-neutral-900 text-base">
                        {sub.name}{' '}
                        <span className="text-neutral-400 font-normal">({sub.state})</span>
                      </p>
                    )}

                    {/* Current vs submitted */}
                    <div className="mt-2 space-y-1">
                      {sub.authority?.websiteUrl && (
                        <p className="text-xs text-neutral-500">
                          Current:{' '}
                          <span className="text-neutral-700">{sub.authority.websiteUrl}</span>
                        </p>
                      )}
                      <p className="text-xs">
                        Suggested:{' '}
                        <a
                          href={sub.submittedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-700 break-all"
                        >
                          {sub.submittedUrl}
                        </a>
                      </p>
                    </div>

                    {/* Submitter */}
                    <p className="text-xs text-neutral-400 mt-2">
                      By{' '}
                      {sub.submittedBy
                        ? `${sub.submittedBy.firstName} ${sub.submittedBy.lastName} (${sub.submittedBy.email})`
                        : `anonymous`}
                      {' · '}
                      {new Date(sub.createdAt).toLocaleDateString()}
                    </p>
                    {sub.notes && (
                      <p className="text-xs text-neutral-500 mt-1 italic">{sub.notes}</p>
                    )}
                  </div>

                  {/* Status badge or actions */}
                  <div className="shrink-0">
                    {sub.status !== 'pending' ? (
                      <span
                        className={`px-2.5 py-1 text-xs font-medium rounded-full capitalize ${STATUS_BADGE[sub.status]}`}
                      >
                        {sub.status}
                      </span>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        <button
                          onClick={() => reviewMutation.mutate({ id: sub.id, action: 'approve' })}
                          disabled={reviewMutation.isPending}
                          className="px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => {
                            setRejecting(sub.id);
                            setRejectNote('');
                          }}
                          className="px-3 py-1.5 text-xs font-medium text-neutral-600 bg-neutral-100 rounded-lg hover:bg-neutral-200 transition-colors"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Reject with note inline */}
                {rejecting === sub.id && (
                  <div className="mt-3 pt-3 border-t border-neutral-100">
                    <input
                      type="text"
                      value={rejectNote}
                      onChange={(e) => setRejectNote(e.target.value)}
                      placeholder="Optional reason for rejection…"
                      className="w-full px-3 py-2 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 mb-2"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() =>
                          reviewMutation.mutate({
                            id: sub.id,
                            action: 'reject',
                            notes: rejectNote || undefined,
                          })
                        }
                        disabled={reviewMutation.isPending}
                        className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                      >
                        Confirm rejection
                      </button>
                      <button
                        onClick={() => setRejecting(null)}
                        className="px-3 py-1.5 text-xs text-neutral-500 hover:text-neutral-700"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 text-base border border-neutral-200 rounded-lg disabled:opacity-40 hover:bg-neutral-50"
            >
              Previous
            </button>
            <span className="text-base text-neutral-500">
              Page {page} of {pagination.pages}
            </span>
            <button
              disabled={page >= pagination.pages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 text-base border border-neutral-200 rounded-lg disabled:opacity-40 hover:bg-neutral-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
